/**
 * Batch detail + traceability (§12, §15, §40.3/§40.5 — Tasks 2-3). Shows the
 * batch summary, live per-order progress (labelled / printed / packed / failed /
 * voided), and the document + lifecycle actions:
 *   - print the picking list (both layouts) + every order's packing slip in one run;
 *   - LABEL_FIRST: run the carrier-label batch up front (mock), then resume/retry;
 *   - per-order: pack at the bench, retry a failed label, void an unused label, skip.
 * Every action re-reads the batch, so an interrupted/partial batch is always shown
 * accurately and is resumable (§40.3). Carriers + printing are mocked here.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, type BatchDetailData, type BatchItemRow } from "../lib/api.js";
import { batchStatusTone, batchStatusLabel, batchItemTone, batchItemLabel, modeLabel } from "../lib/batchStatus.js";
import { humanizeServiceCode } from "../lib/format.js";
import { countryName } from "../lib/countries.js";
import { Badge, Button, Icon, EmptyState, ErrorState, SkeletonRows, useToast } from "../components/ui/index.js";

const openDoc = (url: string) => window.open(url, "_blank", "noopener");

/** §15 summary counters across the batch's items. */
function Counters({ data }: { data: BatchDetailData }) {
  const c = data.batch.counts;
  const cells: Array<{ label: string; value: number; tone: string }> = [
    { label: "Pending", value: c.PENDING ?? 0, tone: "new" },
    { label: "Labelled", value: (c.LABEL_CREATED ?? 0) + (c.PRINTED ?? 0), tone: "routed" },
    { label: "Packed", value: c.packed, tone: "ready" },
    { label: "Failed", value: c.FAILED ?? 0, tone: "exception" },
    { label: "Voided / skipped", value: (c.VOIDED ?? 0) + (c.SKIPPED ?? 0), tone: "cancelled" },
  ];
  return (
    <div className="batch-counters">
      {cells.map((cell) => (
        <div key={cell.label} className={`batch-counter batch-counter--${cell.tone}`}>
          <span className="batch-counter__value tnum">{cell.value}</span>
          <span className="batch-counter__label">{cell.label}</span>
        </div>
      ))}
    </div>
  );
}

export function BatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState<BatchDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    if (!id) return;
    setError(null);
    return api
      .getBatch(id)
      .then((d) => setData(d))
      .catch(() => setError("Couldn’t load this batch."));
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const run = useCallback(
    async (fn: () => Promise<unknown>, ok: string) => {
      if (!id) return;
      setBusy(true);
      try {
        await fn();
        await reload();
        toast(ok, "success");
      } catch {
        toast("That action didn’t complete. Retry in a moment.", "danger");
      } finally {
        setBusy(false);
      }
    },
    [id, reload, toast],
  );

  if (error) return <ErrorState title="Couldn’t load this batch" onRetry={() => void reload()} />;
  if (!data || !id) {
    return (
      <section className="batch">
        <div className="table-card">
          <SkeletonRows rows={6} cols={5} />
        </div>
      </section>
    );
  }

  const { batch, items } = data;
  const labelFirst = batch.mode === "LABEL_FIRST";
  const hasOutstanding = items.some((it) => it.labelStatus === "PENDING" || it.labelStatus === "FAILED");

  return (
    <section className="batch">
      <div className="page-head">
        <div className="page-head__title">
          <button type="button" className="batch-back" onClick={() => navigate("/batches")}>
            <Icon name="chevronLeft" /> Batches
          </button>
          <h1 className="mono">{batch.id}</h1>
          <Badge title="Fulfilment mode">{modeLabel(batch.mode)}</Badge>
          <span className={`chip chip--${batchStatusTone(batch.status)}`}>
            <span className="chip__dot" aria-hidden="true" />
            {batchStatusLabel(batch.status)}
          </span>
        </div>
      </div>
      {batch.note && <p className="batch-note">{batch.note}</p>}

      <Counters data={data} />

      {/* Documents + lifecycle actions. */}
      <div className="batch-actions">
        <Button variant="secondary" size="sm" icon="clipboard" onClick={() => openDoc(`/print/picking-list/${batch.id}`)}>
          Picking list
        </Button>
        <Button variant="secondary" size="sm" icon="printer" onClick={() => openDoc(`/print/packing-slips/${batch.id}`)}>
          Packing slips
        </Button>
        {labelFirst && (
          <Button
            variant="primary"
            size="sm"
            icon="layers"
            disabled={busy || !hasOutstanding}
            onClick={() => void run(() => api.printBatchLabels(batch.id), "Label batch run")}
          >
            Run label batch
          </Button>
        )}
        {hasOutstanding && (
          <Button
            variant="secondary"
            size="sm"
            icon="rotate"
            disabled={busy}
            onClick={() => void run(() => api.resumeBatch(batch.id), "Resumed — outstanding items re-run")}
          >
            Resume / retry failed
          </Button>
        )}
      </div>

      {/* Per-order traceability (§40.5). */}
      <div className="table-card">
        <div className="table-scroll">
          <table className="q-table" aria-label="Batch orders">
            <thead>
              <tr>
                <th>Order</th>
                <th>Destination</th>
                <th>Carrier</th>
                <th>Status</th>
                <th>Tracking</th>
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <BatchItemRowView key={it.orderId} it={it} busy={busy} onPack={() => navigate(`/pack?order=${encodeURIComponent(it.orderId)}`)} run={run} batchId={batch.id} />
              ))}
            </tbody>
          </table>
        </div>
        {items.length === 0 && <EmptyState title="No orders in this batch" />}
      </div>
    </section>
  );
}

function BatchItemRowView({
  it,
  busy,
  batchId,
  onPack,
  run,
}: {
  it: BatchItemRow;
  busy: boolean;
  batchId: string;
  onPack: () => void;
  run: (fn: () => Promise<unknown>, ok: string) => Promise<void>;
}) {
  const done = it.labelStatus === "VOIDED" || it.labelStatus === "SKIPPED";
  const canVoid = (it.labelStatus === "LABEL_CREATED" || it.labelStatus === "PRINTED") && !it.packed;
  const canRetry = it.labelStatus === "FAILED";
  const canSkip = it.labelStatus === "PENDING" || it.labelStatus === "FAILED";
  return (
    <tr className="q-row">
      <td>
        <span className="q-order mono">{it.orderName}</span>
        {it.errorDetail && <div className="q-exc truncate" title={it.errorDetail}>{it.errorDetail}</div>}
      </td>
      <td>
        <span className="truncate">{countryName(it.destinationCountry) || it.destinationCountry}</span>
      </td>
      <td>
        {it.carrierCode ? (
          <span className="q-carrier">
            {it.carrierCode}
            {it.serviceCode && <span className="q-carrier__svc truncate"> · {humanizeServiceCode(it.serviceCode)}</span>}
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td>
        <span className={`chip chip--${batchItemTone(it.labelStatus)}`}>
          <span className="chip__dot" aria-hidden="true" />
          {batchItemLabel(it.labelStatus)}
        </span>
      </td>
      <td className="mono">{it.trackingNumber ?? <span className="muted">—</span>}</td>
      <td className="q-actions">
        <div className="batch-row-actions">
          {!done && it.labelStatus !== "PACKED" && (
            <Button size="sm" variant="secondary" icon="package" onClick={onPack}>
              Pack
            </Button>
          )}
          <Button size="sm" variant="ghost" icon="printer" onClick={() => window.open(`/print/packing-slip/${it.orderId}`, "_blank", "noopener")}>
            Slip
          </Button>
          {canRetry && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(() => api.retryBatchItem(batchId, it.orderId), "Retried")}>
              Retry
            </Button>
          )}
          {canVoid && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(() => api.voidBatchItem(batchId, it.orderId), "Label voided")}>
              Void
            </Button>
          )}
          {canSkip && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(() => api.skipBatchItem(batchId, it.orderId), "Skipped")}>
              Skip
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
